/* ============================================================
   sl-swipe.js  —  "swipe-by studiolab"

   The carousel itself is pure CSS (overflow-x + scroll-snap),
   emitted by blocks/sl-swipe.liquid. This file exists only to
   build the pagination dots, which cannot be authored in Liquid
   because they must sit OUTSIDE the scrolling container — a dot
   bar rendered inside it would scroll away with the slides.

   - Host is resolved as marker.parentElement, so the same code
     works at section level and at any nested group level.
   - matchMedia driven: dots are built when the breakpoint is
     matched and torn down when it is not. Survives rotation and
     window resizing in both directions.
   - Active index is measured from real element geometry
     (getBoundingClientRect), never from an assumed slide pitch,
     so it stays correct for every slide-width setting.
   - Idempotent and Theme-Editor aware (shopify:section:load /
     :unload / :block:select).
   ============================================================ */
(function () {
  'use strict';

  /* Each block instance emits its own <script> tag, so this file
     can be executed more than once on a page. Everything below is
     idempotent anyway, but the guard keeps the global listeners
     registered exactly once. */
  if (window.__slSwipeLoaded) return;
  window.__slSwipeLoaded = true;

  var MARKER = '[data-sl-swipe]';

  function reducedMotion() {
    return !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /* Direct children of the host that count as slides: everything
     except the marker itself and non-rendering nodes. */
  function slidesOf(host, marker) {
    var out = [];
    var kids = host.children;
    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (el.nodeType !== 1) continue;
      if (el === marker) continue;
      if (el.hasAttribute('data-sl-swipe') || el.hasAttribute('data-sl-swipe-off')) continue;
      if (el.hasAttribute('hidden')) continue;
      var tag = el.tagName;
      if (tag === 'STYLE' || tag === 'SCRIPT' || tag === 'LINK' || tag === 'TEMPLATE') continue;
      out.push(el);
    }
    return out;
  }

  function paddingStart(host) {
    var cs = window.getComputedStyle(host);
    var value = cs.paddingInlineStart || cs.paddingLeft;
    return parseFloat(value) || 0;
  }

  function removeBar(id) {
    var old = document.querySelectorAll('[data-sl-swipe-dotbar="' + id + '"]');
    for (var i = 0; i < old.length; i++) {
      if (old[i].parentNode) old[i].parentNode.removeChild(old[i]);
    }
  }

  /* ---------- one carousel instance ---------------------------------- */

  function setup(marker) {
    if (marker.__slSwipe) return;

    var host = marker.parentElement;
    var id = marker.getAttribute('data-sl-swipe');
    if (!host || !id) return;

    var state = {
      id: id,
      marker: marker,
      host: host,
      bar: null,
      dots: [],
      slides: [],
      last: -1,
      raf: 0,
      mobileEnabled: marker.getAttribute('data-sl-swipe-mobile') !== '0',
      desktopEnabled: marker.getAttribute('data-sl-swipe-desktop') === '1',
      wantDots: false,
      pos: 'inline',
      align: 'center',
      style: 'peek_center',
      retention: false,
      active: false,
      mq: null,
      onScroll: null,
      onResize: null,
      onMq: null
    };
    marker.__slSwipe = state;

    var bp = parseInt(marker.getAttribute('data-sl-swipe-bp'), 10);
    if (!bp) bp = 749;
    state.mq = window.matchMedia('(max-width: ' + bp + 'px)');

    function currentConfig() {
      var device = state.mq.matches ? 'mobile' : 'desktop';
      var enabled = device === 'mobile' ? state.mobileEnabled : state.desktopEnabled;
      if (!enabled) return null;
      return {
        wantDots: marker.getAttribute('data-sl-swipe-dots-' + device) === '1',
        pos: marker.getAttribute('data-sl-swipe-pos-' + device) || 'inline',
        align: marker.getAttribute('data-sl-swipe-align-' + device) || 'center',
        style: marker.getAttribute('data-sl-swipe-style-' + device) || 'peek_center',
        retention:
          marker.hasAttribute('data-sl-retention') &&
          marker.getAttribute('data-sl-retention-' + device) === '1'
      };
    }

    function clearBleedOffset() {
      state.host.style.removeProperty('--sl-swipe-bleed-offset');
    }

    function alignBleedToViewport() {
      if (state.style !== 'bleed' || !state.active) {
        clearBleedOffset();
        return;
      }

      var cs = window.getComputedStyle(state.host);
      var rect = state.host.getBoundingClientRect();
      var isRtl = cs.direction === 'rtl';
      var currentMargin = parseFloat(isRtl ? cs.marginRight : cs.marginLeft) || 0;
      var unshiftedInset = isRtl
        ? window.innerWidth - rect.right - currentMargin
        : rect.left - currentMargin;

      state.host.style.setProperty('--sl-swipe-bleed-offset', -unshiftedInset + 'px');
    }

    /* --- active dot ------------------------------------------------- */

    function activeIndex() {
      var hostRect = state.host.getBoundingClientRect();
      var probe =
        state.align === 'center'
          ? hostRect.left + hostRect.width / 2
          : hostRect.left + paddingStart(state.host);

      var best = 0;
      var bestDist = Infinity;

      for (var i = 0; i < state.slides.length; i++) {
        var r = state.slides[i].getBoundingClientRect();
        var point = state.align === 'center' ? r.left + r.width / 2 : r.left;
        var dist = Math.abs(point - probe);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      return best;
    }

    function paint() {
      if (!state.dots.length) return;
      var idx = activeIndex();
      if (idx === state.last) return;
      state.last = idx;

      for (var i = 0; i < state.dots.length; i++) {
        if (i === idx) {
          state.dots[i].classList.add('is-active');
          state.dots[i].setAttribute('aria-current', 'true');
        } else {
          state.dots[i].classList.remove('is-active');
          state.dots[i].removeAttribute('aria-current');
        }
      }
    }

    function schedulePaint() {
      if (state.raf) cancelAnimationFrame(state.raf);
      state.raf = requestAnimationFrame(function () {
        state.raf = 0;
        paint();
      });
    }

    /* --- scroll to a slide ------------------------------------------ */

    function goTo(index) {
      var slide = state.slides[index];
      if (!slide) return;

      var hostRect = state.host.getBoundingClientRect();
      var r = slide.getBoundingClientRect();
      var delta;

      if (state.align === 'center') {
        delta = r.left + r.width / 2 - (hostRect.left + hostRect.width / 2);
      } else {
        delta = r.left - hostRect.left - paddingStart(state.host);
      }

      var left = state.host.scrollLeft + delta;

      if (state.host.scrollTo) {
        state.host.scrollTo({ left: left, behavior: reducedMotion() ? 'auto' : 'smooth' });
      } else {
        state.host.scrollLeft = left;
      }
    }

    /* --- build / destroy the dot bar --------------------------------- */

    function build() {
      destroy();

      state.slides = slidesOf(state.host, state.marker);
      if (state.slides.length < 2) return;

      var bar = document.createElement('div');
      bar.setAttribute('data-sl-swipe-dotbar', state.id);
      bar.setAttribute('role', 'group');
      bar.setAttribute('aria-label', 'Navegação do carrossel');

      for (var i = 0; i < state.slides.length; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', 'Ir para o slide ' + (i + 1));
        bar.appendChild(dot);

        (function (index, node) {
          node.addEventListener('click', function () {
            goTo(index);
          });
        })(i, dot);
      }

      /* Overlay mode pins the bar to the bottom of the whole
         section (the reference behaviour). Inline mode drops it
         directly after the scroller, which is what keeps the dots
         next to their carousel when it sits inside a group. */
      if (state.pos === 'overlay' && !state.retention) {
        var section = state.marker.closest('.shopify-section') || state.host.parentNode;
        section.appendChild(bar);
      } else if (state.host.parentNode) {
        state.host.parentNode.insertBefore(bar, state.host.nextSibling);
      } else {
        return;
      }

      state.bar = bar;
      state.dots = [].slice.call(bar.querySelectorAll('button'));
      state.last = -1;

      state.onScroll = function () {
        schedulePaint();
      };
      state.host.addEventListener('scroll', state.onScroll, { passive: true });

      paint();
    }

    function destroy() {
      if (state.raf) {
        cancelAnimationFrame(state.raf);
        state.raf = 0;
      }
      if (state.onScroll) {
        state.host.removeEventListener('scroll', state.onScroll);
        state.onScroll = null;
      }
      removeBar(state.id);
      state.bar = null;
      state.dots = [];
      state.slides = [];
      state.last = -1;
    }

    state.destroy = destroy;
    state.goTo = goTo;
    state.build = build;

    /* --- lifecycle ---------------------------------------------------- */

    function apply() {
      /* The section may have been re-rendered under us by the Theme
         Editor. If our host is gone, unhook the global listeners
         instead of operating on a detached node. */
      if (!document.contains(state.marker)) {
        teardown(state);
        return;
      }
      var config = currentConfig();
      state.active = !!config;
      if (config) {
        state.wantDots = config.wantDots;
        state.pos = config.pos;
        state.align = config.align;
        state.style = config.style;
        state.retention = config.retention;
        alignBleedToViewport();
        if (state.wantDots) build();
        else destroy();
      } else {
        clearBleedOffset();
        destroy();
      }
    }

    state.apply = apply;

    state.onMq = function () {
      apply();
    };
    if (state.mq.addEventListener) {
      state.mq.addEventListener('change', state.onMq);
    } else if (state.mq.addListener) {
      state.mq.addListener(state.onMq);
    }

    state.onResize = function () {
      if (!document.contains(state.marker)) {
        teardown(state);
        return;
      }
      alignBleedToViewport();
      state.last = -1;
      schedulePaint();
    };
    window.addEventListener('resize', state.onResize, { passive: true });

    apply();
  }

  function teardown(state) {
    if (!state) return;
    if (state.destroy) state.destroy();
    if (state.host) state.host.style.removeProperty('--sl-swipe-bleed-offset');
    if (state.onMq && state.mq) {
      if (state.mq.removeEventListener) {
        state.mq.removeEventListener('change', state.onMq);
      } else if (state.mq.removeListener) {
        state.mq.removeListener(state.onMq);
      }
      state.onMq = null;
    }
    if (state.onResize) {
      window.removeEventListener('resize', state.onResize);
      state.onResize = null;
    }
    if (state.marker) state.marker.__slSwipe = null;
  }

  /* ---------- boot ---------------------------------------------------- */

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var markers = root.querySelectorAll(MARKER);
    for (var i = 0; i < markers.length; i++) setup(markers[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    if (!target || !target.querySelectorAll) return;
    var markers = target.querySelectorAll(MARKER);
    for (var i = 0; i < markers.length; i++) teardown(markers[i].__slSwipe);
  });

  /* Selecting a slide in the Theme Editor should bring it into
     view — a horizontal scroller does not do that on its own. */
  document.addEventListener('shopify:block:select', function (event) {
    var target = event.target;
    if (!target || !target.parentElement) return;

    var host = target.parentElement;
    var marker = host.querySelector(':scope > ' + MARKER);
    if (!marker || !marker.__slSwipe) return;

    var state = marker.__slSwipe;
    if (!state.active) return;

    var slides = slidesOf(host, marker);
    for (var i = 0; i < slides.length; i++) {
      if (slides[i] === target) {
        state.slides = slides;
        state.goTo(i);
        return;
      }
    }
  });
})();
